import { useCallback, useMemo } from 'react';
import { Routes, Route, Navigate, Outlet, useParams, useNavigate, useLocation } from 'react-router-dom';
import { BreadcrumbNav, Button, Skeleton, StatusMessage, useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import InstanceHeader from '../components/InstanceHeader.jsx';
import { useInstance } from '../hooks/instance';
import { useIntegrationsPermissions } from '../hooks';
import { buildInstanceRoutePaths } from '../utils';
import BasicInfoTab from './instance/BasicInfoTab.jsx';
import LogsTab from './instance/LogsTab.jsx';
import InstanceAuditTab from './instance/InstanceAuditTab.jsx';
import InstanceUsageTab from './instance/InstanceUsageTab.jsx';
import InstanceCapabilitiesTab from './instance/InstanceCapabilitiesTab.jsx';
import AccessTab from './instance/AccessTab.jsx';
import AuthenticationTab from './instance/AuthenticationTab.jsx';
import InstanceTabPlaceholder from './instance/InstanceTabPlaceholder.jsx';
import { AlertTriangle } from 'lucide-react';

const TABS = [
  { id: 'basic', path: 'basic', labelKey: 'integrations.instance.tabs.basic' },
  { id: 'authentication', path: 'authentication', labelKey: 'integrations.instance.tabs.authentication' },
  { id: 'capabilities', path: 'capabilities', labelKey: 'integrations.instance.tabs.capabilities' },
  { id: 'access', path: 'access', labelKey: 'integrations.instance.tabs.access' },
  { id: 'usage', path: 'usage', labelKey: 'integrations.instance.tabs.usage' },
  { id: 'logs', path: 'logs', labelKey: 'integrations.instance.tabs.logs' },
  { id: 'audit', path: 'audit', labelKey: 'integrations.instance.tabs.audit' },
];

/** Real tab elements land here as each story ships; the rest fall back to the placeholder. */
const TAB_ELEMENTS = {
  basic: <BasicInfoTab />,
  logs: <LogsTab />,
  audit: <InstanceAuditTab />,
  usage: <InstanceUsageTab />,
  capabilities: <InstanceCapabilitiesTab />,
  access: <AccessTab />,
  authentication: <AuthenticationTab />,
};

/** Resolve a tab's element: shipped tab → placeholder for the not-yet-built tabs. */
const tabElement = (tab) =>
  TAB_ELEMENTS[tab.id] ?? <InstanceTabPlaceholder labelKey={tab.labelKey} ticket="CT-3897" />;

// Content inset is 1.5rem (`px-6`) — matches Provider Detail; no PageContainer
// max-width gutter that would double the side air against the shell.
const PAGE_WRAP = 'flex-1 overflow-y-auto bg-surface-base scrollbar-aimsos-auto-sm';
const PAGE_INNER = 'px-6';

// Keep the Instance shell visually identical to Provider Detail while retaining
// the ticket-mandated @aimsos/ui Button primitive for routed tabs.
const TAB_BASE = [
  '!h-auto !rounded-b-none !rounded-t-md !border-x-0 !border-t-0 !border-b-2',
  '!px-3.5 !py-[11px] text-[13px] !font-semibold tracking-[-0.005em]',
  'focus-visible:!ring-2 focus-visible:!ring-[#2B7FFF]/50 focus-visible:!ring-inset',
].join(' ');
const TAB_IDLE =
  '!border-b-transparent !bg-transparent !text-content-secondary hover:!bg-white/[0.03] hover:!text-content';
const TAB_ACTIVE =
  '!border-b-[#2173FF] !bg-transparent !text-[#2173FF] hover:!bg-transparent';

/**
 * Instance Detail layout (CT-4188). Admin-only (route guard is a TODO(CT-3900)
 * seam). Owns `useInstance(providerSlug, instanceId)`, the sticky header, and the
 * URL-routed tab bar. Tabs read the instance via `useOutletContext()`.
 */
const InstanceDetailLayout = () => {
  const { t } = useTranslation('admin-studio');
  const { providerSlug, instanceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { canManageIntegrations } = useIntegrationsPermissions();
  const { instance, isLoading, error, notFound } = useInstance(providerSlug, instanceId);

  const { providerBase, integrationsBase, instanceBase, activePath, basicPath } =
    buildInstanceRoutePaths(location.pathname, providerSlug, instanceId);

  const handleRotate = useCallback(
    () => toast.info({ message: t('integrations.instance.stubs.rotate'), canClose: true }),
    [toast, t],
  );
  const handleDisconnect = useCallback(
    () => toast.info({ message: t('integrations.instance.stubs.disconnect'), canClose: true }),
    [toast, t],
  );
  const outletContext = useMemo(() => ({ instance }), [instance]);

  // TODO(CT-3900 / ARP-804): real RBAC. V1 `canManageIntegrations` is always true,
  // so nobody is redirected yet. When the gate flips false, send end users back to
  // the provider Instances tab.
  if (!canManageIntegrations) {
    return <Navigate to={`${providerBase}/instances`} replace />;
  }

  if (isLoading) {
    return (
      <main className={PAGE_WRAP}>
        <div className={`${PAGE_INNER} py-6`}>
          <Skeleton className="h-24 w-full" />
        </div>
      </main>
    );
  }
  if (notFound) {
    return (
      <main className={PAGE_WRAP}>
        <div className={`${PAGE_INNER} py-6`}>
          <StatusMessage
            icon={AlertTriangle}
            title={t('integrations.instance.notFound.title')}
            subtitle={t('integrations.instance.notFound.subtitle')}
          />
        </div>
      </main>
    );
  }
  if (error || !instance) {
    return (
      <main className={PAGE_WRAP}>
        <div className={`${PAGE_INNER} py-6`}>
          <StatusMessage icon={AlertTriangle} title={t('common.requestFailed')} subtitle={error} />
        </div>
      </main>
    );
  }

  return (
    <main className={PAGE_WRAP}>
      <div className={PAGE_INNER}>
        <div className="pt-3.5">
          <BreadcrumbNav
            size="sm"
            items={[
              { label: t('integrations.home.title'), path: integrationsBase },
              { label: instance.providerName, path: providerBase },
              { label: instance.name, path: instanceBase },
            ]}
            onNavigate={(path) => navigate(path)}
          />
        </div>

        <div className="sticky top-0 z-20 -mx-6 mt-4 bg-surface-base/95 px-6 pt-6 backdrop-blur">
          <InstanceHeader
            instance={instance}
            onRotate={handleRotate}
            onDisconnect={handleDisconnect}
          />
          <nav
            role="tablist"
            aria-label={t('integrations.instance.tabs.ariaLabel')}
            className="-mx-6 mt-4 flex flex-wrap gap-1 border-b border-edge px-6"
          >
            {TABS.map((tab) => {
              const active = activePath === tab.path;
              return (
                <Button
                  key={tab.id}
                  role="tab"
                  aria-selected={active}
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(`${instanceBase}/${tab.path}`)}
                  className={[TAB_BASE, active ? TAB_ACTIVE : TAB_IDLE].join(' ')}
                >
                  {t(tab.labelKey)}
                </Button>
              );
            })}
          </nav>
        </div>

        <div className="flex flex-col gap-4 pb-6 pt-4">
          <Routes>
            <Route element={<Outlet context={outletContext} />}>
              <Route index element={<Navigate to={basicPath} replace />} />
              {TABS.map((tab) => (
                <Route key={tab.id} path={tab.path} element={tabElement(tab)} />
              ))}
              <Route path="*" element={<Navigate to={basicPath} replace />} />
            </Route>
          </Routes>
        </div>
      </div>
    </main>
  );
};

export default InstanceDetailLayout;
